<template>
  <control-base
    :config="conf"
    :dataType="dataType"
    :parentName="$options.name"
    @state-selected="addState"
    @state-removed="removeState"
    @loaded="$emit('loaded', $event)"
    ref="base"
  >
    <template v-slot:filters>
      <div class="flex-full">
        <p>Background colour for agents on inbound:</p>
        <span style="width: 50px; height: 10px"></span>
        <colour-picker
          :color="config.inboundColor"
          v-model="config.inboundColor"
        />
      </div>

      <div class="flex-full">
        <p>Show Robots:</p>
        <input type="checkbox" v-model="conf.showRobots" />
      </div>

      <div class="flex-full">
        <p>Breaks Mode:</p>
        <input type="checkbox" v-model="conf.breakMode" />
      </div>

      <div class="flex-full">
        <p>Use state colour as background:</p>
        <input type="checkbox" v-model="conf.useStateBackground" />
      </div>

      <div class="flex-full">
        <p>Show wrap %:</p>
        <input type="checkbox" v-model="conf.showWrap" />
      </div>

      <div class="flex-full">
        <p>Show icon on each tile:</p>
        <input type="checkbox" v-model="conf.showIcon" />
      </div>

      <div class="flex-full">
        <p class="vertical-center">Agent Sorting:</p>
        <drop-down
          class="w-full"
          :options="sortTypes"
          :selected="conf.selectedSort"
          subProp="name"
          placeholder="By State then Time"
          @updateOption="selectSort"
          :closeOnOutsideClick="true"
        />
      </div>

      <reorder-array
        title="State Order"
        :values="mappedStates"
        display=""
        @order-changed="updateOrder"
        v-if="conf.selectedSort.value == 4 || conf.selectedSort.value == 5"
      >
      </reorder-array>

      <div style="border: 1px solid gray; padding: 5px; margin-top: 5px">
        <p>State Colours</p>
        <div
          class="flex-full"
          v-for="state in conf.chosenStates"
          :key="state.state"
        >
          <p class="vertical-center">
            {{ getStateTextByValue(state.state) }}
          </p>
          <colour-picker
            @input="recompute = true"
            :color="state.color"
            v-model="state.color"
          />
        </div>
      </div>
    </template>
    <template v-slot:control>
      <div class="agent-container" :style="background">
        <div id="inner-agent" ref="agentContainer" class="agent-view">
          <template v-for="agent in agents" stagger="100">
            <agent-break-card
              v-if="conf.breakMode"
              :key="agent.AgentName"
              :agentInfo="agent"
              :cardSize="cardSize"
            />

            <agent-card
              v-else
              :showIcon="conf.showIcon"
              :showWrap="conf.showWrap"
              :fullState="config.useStateBackground"
              :agentInfo="agent"
              :inboundColor="config.inboundColor"
              :key="agent.AgentName"
              :cardSize="cardSize"
              :campaignType="getCampaignType(agent.campaign)"
              :color="agent.color"
              :resize="resize"
              @resized="count"
              class="agent-card"
            />
          </template>
        </div>
      </div>
    </template>
  </control-base>
</template>

<script>
import { AgentBreakCard, AgentCard } from "@/components";

export default {
  name: "AgentsTileDisplay", // Component name must be exactly the same as the name you give it in the toolbox minus the spaces

  components: {
    AgentBreakCard,
    AgentCard,
  },

  props: {
    config: {
      required: true,
      value: Object,
    },

    shouldResize: {
      required: false,
      value: Boolean,
    },
  },

  data() {
    return {
      dataType: "agent",
      sortTypes: [
        { name: "By Time ascending", value: 0 },
        { name: "By Time descending", value: 1 },
        { name: "By Name ascending", value: 2 },
        { name: "By Name descending", value: 3 },
        { name: "By State then Time", value: 4 },
        { name: "By State then Name", value: 5 },
      ],
      recompute: false,
      cardSize: [],
      resize: false,
      resizedRows: 0,
    };
  },

  mounted() {
    this.registerDependencies();
  },

  computed: {
    conf() {
      return this.config;
    },

    mappedStates() {
      var states = this.$constants.AgentStates;
      return this.conf.agentSortOrder.map((x) => {
        var index = states.findIndex((z) => z.value == x);
        return states[index].name;
      });
    },

    layoutLocked() {
      return this.$store.getters.locked;
    },

    background() {
      return { backgroundColor: this.config.Background };
    },

    unsortedAgents() {
      return this.$store.getters.agentData
        .filter(
          (x) =>
            this.conf.campaignNames.includes(x.campaign) &&
            this.conf.agentStates.includes(x.agentStateNum) &&
            (this.conf.showRobots ? true : !x.agentName.startsWith("Robot")) &&
            (this.conf.showGDMSMonitor ? true : !x.agentName.startsWith("gdmsmonitor")) &&
            (!this.conf.breakMode ? true : x.agentStateNum == 2 || x.agentStateNum == 0)
        )
        .map((x) => ({
          ...x,
          color: this.conf.chosenStates.find((z) => z.state == x.agentStateNum)
            .color,
          timestampCurrentState: new Date(Date.parse(x.timestampCurrentState)),
        }));
    },

    agents() {
      /* eslint-disable-next-line */
      if (this.recompute) this.recompute = false;

      let agentList = this.unsortedAgents;
      switch (this.conf.selectedSort.value) {
        case 0:
          return agentList.sort((a, b) =>
            a.timestampCurrentState > b.timestampCurrentState ? -1 : 1
          );
        case 1:
          return agentList.sort((a, b) =>
            a.timestampCurrentState > b.timestampCurrentState ? 1 : -1
          );
        case 2:
          return agentList.sort((a, b) =>
            a.agentName.localeCompare(b.agentName)
          );
        case 3:
          return agentList.sort((a, b) =>
            b.agentName.localeCompare(a.agentName)
          );
        case 4:
          return agentList.sort((a, b) => {
            var indexA = this.conf.agentSortOrder.findIndex(
              (x) => x == a.agentStateNum
            );

            var indexB = this.conf.agentSortOrder.findIndex(
              (x) => x == b.agentStateNum
            );

            if (indexA < indexB) return -1;
            else if (indexA > indexB) return 1;
            else if (a.timestampCurrentState > b.timestampCurrentState)
              return 1;
            else if (a.timestampCurrentState < b.timestampCurrentState)
              return -1;
          });
        case 5:
          return agentList.sort((a, b) => {
            var indexA = this.conf.agentSortOrder.findIndex(
              (x) => x == a.agentStateNum
            );
            var indexB = this.conf.agentSortOrder.findIndex(
              (x) => x == b.agentStateNum
            );

            if (indexA < indexB) return -1;
            else if (indexA > indexB) return 1;
            else return a.agentName.localeCompare(b.agentName);
          });
        default:
          return agentList;
      }
    },
  },

  watch: {
    agents: {
      deep: true,
      handler() {
        let size = this.getCardSize();
        if (size[0] != this.cardSize[0] || size[1] != this.cardSize[1]) {
          this.cardSize = size;
        }
      },
    },

    config: {
      deep: true,
      handler() {
        let size = this.getCardSize();
        if (size[0] != this.cardSize[0] || size[1] != this.cardSize[1]) {
          this.cardSize = size;
        }
        this.resize = true;
      },
    },

    shouldResize() {
      if (this.shouldResize) {
        this.recompute = true;
        this.$emit("resized");
      }
    },
  },

  methods: {
    count() {
      this.resizedRows += 1;
      if (this.resizedRows == this.agents.length) {
        this.resizedRows = 0;
        this.resize = false;
      }
    },

    registerDependencies() {
      var props = [
        "AgentStateNum",
        "TimestampCurrentState",
        "TotalCalls",
        "TotalInbound",
        "TotalOutbound",
        "BreakReason",
        "Campaign",
        "WrapPercent",
      ];
      props.forEach((x) => this.$store.getters.agentArgs.addProperty(x));

      var states = [0, 1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 15, 16, 17];
      states.forEach((x) => this.$store.getters.agentArgs.addAgentState(x));
    },

    updateOrder(cols) {
      var states = this.$constants.AgentStates;
      this.conf.agentSortOrder = cols.map((x) => {
        var index = states.findIndex((z) => z.name == x);
        return states[index].value;
      });

      this.$emit("configChange", this.conf);
    },

    selectSort(item) {
      this.conf.selectedSort = item;
      this.$emit("configChange", this.conf);
      this.recompute = true;
    },

    getStateTextByValue(val) {
      return this.$constants.AgentStates.find((x) => x.value == val).name;
    },

    getCampaignType(campaignName) {
      return this.$diamond.allowedCampaigns.find((x) => x.name === campaignName)
        .type == "out"
        ? 0
        : 1;
    },

    color(agentColor) {
      return this.config.UseStateBackground ? "#000000" : agentColor;
    },

    getCardSize() {
      var numRows = 1;
      if (!this.$refs.agentContainer) return;
      let containerX = this.$refs.agentContainer.clientWidth;
      let containerY = this.$refs.agentContainer.clientHeight;
      let numItems = this.agents.length;

      // eslint-disable-next-line no-constant-condition
      var tileWidth = 0;
      var tileHeight = 0;

      while (tileWidth >= 0 && tileHeight >= 0) {
        tileWidth = containerX / Math.ceil(numItems / numRows);
        while (tileWidth * Math.ceil(numItems / numRows) >= containerX) {
          tileWidth -= 1;
        }
        if (containerY / numRows > tileWidth) {
          numRows++;
        } else {
          tileHeight = containerY / numRows;
          return [tileWidth, tileHeight];
        }
      }
    },

    addState(state) {
      var stateIndex = this.conf.chosenStates.findIndex(
        (x) => x.state == state
      );
      if (stateIndex == -1)
        this.conf.chosenStates.push({
          state: state,
          color: this.getStateColour(state),
        });

      stateIndex = this.conf.agentSortOrder.findIndex((x) => x == state);
      if (stateIndex == -1) this.conf.agentSortOrder.push(state);
      this.$emit("configChange", this.conf);
    },

    getStateColour(state) {
      switch(state){
        case 3:
          return "#C85C5C"
        case 6:
          return "#96DA79"
        case 7:
          return "#D29A50"
        case 2:
          return "#74E1E2"
      }

      return this.getRandomColor()
    },

    getRandomColor() {
      var letters = "0123456789ABCDEF";
      var color = "#";
      for (var i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },

    removeState(state) {
      var index = this.conf.chosenStates.findIndex((x) => x.state == state);
      var indexSort = this.conf.agentSortOrder.findIndex((x) => x == state);

      this.conf.chosenStates.splice(index, 1);
      this.conf.agentSortOrder.splice(indexSort, 1);

      this.$emit("configChange", this.conf);
    },
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  height: 100%;
}

.filters {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0;
  margin-right: -40px;
  position: relative;
}

.agent-container {
  height: calc(100% - 10px);
  padding: 5px;
  overflow: hidden;
}

.agent-container .agent-view {
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
}
.agent-container .agent-view p {
  margin: 0;
  padding: 0;
}
.agent-container .agent-view h2 {
  margin: 0;
  padding: 0;
}
.agent-container .agent-view h1 {
  margin: 0;
  padding: 0;
}
.agent-container .agent-view h3 {
  margin: 0;
  padding: 0;
}

.vertical-center {
  display: flex;
  align-items: center;
}
</style>